/**
 * LazyLoad
 *
 * Two responsibilities:
 * 1. Promote every `[data-lazy-video]` to a real `<source src>` on the first
 *    user scroll/wheel/touch. Below-fold videos stay off the network until
 *    the user signals engagement, which keeps initial page-load light.
 * 2. Promote each `[data-lazy-gallery]` slide image just-in-time. The first
 *    slide already has a real `src` (with `loading="lazy"`), so the browser
 *    handles its viewport intersection. Subsequent slides hold `data-src`
 *    until Swiper changes slide — we then promote the active and next slide.
 */
export default class LazyLoad {
    constructor() {
        this.initGlobalVideoLoader();
        this.initGalleryLoaders();
    }
    promoteSrc(node) {
        const dataSrc = node.getAttribute('data-src');
        if (!dataSrc)
            return;
        node.setAttribute('src', dataSrc);
        node.removeAttribute('data-src');
    }
    promoteVideo(video) {
        const sources = video.querySelectorAll('source[data-src]');
        if (!sources.length)
            return;
        sources.forEach((source) => this.promoteSrc(source));
        video.removeAttribute('data-lazy-video');
        video.load();
        // autoplay attribute (if present) resumes once metadata is decoded.
    }
    promoteSlide(slide) {
        if (!slide)
            return;
        const img = slide.querySelector('img[data-src]');
        if (img)
            this.promoteSrc(img);
    }
    initGlobalVideoLoader() {
        const lazyVideos = document.querySelectorAll('[data-lazy-video]');
        // Images deferred outside a gallery (e.g. latest-news cards). Galleries
        // handle their own slides via the slide-change hook.
        const deferredImages = Array.from(document.querySelectorAll('img[data-src]')).filter((img) => !img.closest('[data-lazy-gallery]'));
        if (!lazyVideos.length && !deferredImages.length)
            return;
        const onFirstScroll = () => {
            lazyVideos.forEach((video) => this.promoteVideo(video));
            deferredImages.forEach((img) => this.promoteSrc(img));
            window.removeEventListener('scroll', onFirstScroll);
            window.removeEventListener('wheel', onFirstScroll);
            window.removeEventListener('touchstart', onFirstScroll);
        };
        window.addEventListener('scroll', onFirstScroll, { passive: true, once: true });
        window.addEventListener('wheel', onFirstScroll, { passive: true, once: true });
        window.addEventListener('touchstart', onFirstScroll, { passive: true, once: true });
    }
    initGalleryLoaders() {
        const galleries = document.querySelectorAll('[data-lazy-gallery]');
        if (!galleries.length)
            return;
        galleries.forEach((gallery) => {
            const sliderRoot = gallery.querySelector('.slider-default');
            if (!sliderRoot)
                return;
            // Slider.ts fires `slider:slidechange` with the swiper instance.
            sliderRoot.addEventListener('slider:slidechange', (event) => {
                const detail = event.detail;
                const swiper = detail && detail.swiper;
                if (!swiper)
                    return;
                const slides = swiper.slides;
                const activeIdx = swiper.activeIndex;
                // Promote every currently-visible slide plus one preload ahead.
                // slidesPerView can be a number or 'auto'; fall back to 1.
                const slidesPerView = typeof swiper.params.slidesPerView === 'number'
                    ? swiper.params.slidesPerView
                    : 1;
                const visibleCount = Math.ceil(slidesPerView);
                for (let i = 0; i <= visibleCount; i++) {
                    this.promoteSlide(slides[activeIdx + i]);
                }
            });
        });
    }
}
